// Ô chọn vật tư bao bì dùng chung: tìm theo API (debounce), cuộn tải thêm,
// option nhiều dòng.
import { useMemo, useState } from 'react'

import type { PackagingMaterial } from '@/lib/api/packaging-api'
import { LotOptionRow } from '@/components/lots/lot-option-row'
import { Combobox } from '@/components/ui/combobox'
import { useDebounced } from '@/hooks/use-debounced'
import {
    useInfinitePackagingMaterials,
    usePackagingMaterial,
} from '@/hooks/use-packaging-query'
import { formatQty } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'

export interface PickedPackagingMaterial {
    id: string
    name: string
    sku: string
    unit: string
    /** Đơn giá bán vật tư (giá vật tư); >0 → tính tiền vào đơn. */
    sale_price: number
    /** Vật tư đóng gói SP — quyết định pack_size khoá về 1 ở BOM. */
    is_for_packaging: boolean
    /** Theo lô hay theo số lượng — form phiếu nhập ẩn/hiện ô lô theo đây. */
    tracking_mode: PackagingMaterial['tracking_mode']
}

interface Props {
    /** id đã có trong list — sẽ bị filter ra để tránh trùng. */
    excludeIds?: string[]
    onPick: (material: PickedPackagingMaterial) => void
    placeholder?: string
    disabled?: boolean
    /** Ẩn vật tư đóng gói SP (is_for_packaging) — chúng tự vào đơn qua dòng
     * charge_only, không cho thêm tay để tránh trùng/tính tiền 2 lần. */
    excludeForPackaging?: boolean
    /** Giữ lại lựa chọn sau khi chọn (dùng cho ô chọn một, VD BOM / phiếu nhập).
     *  Mặc định false: reset về rỗng để thêm tiếp nhiều dòng. */
    value?: string
    /** Chỉ lấy vật tư đang sử dụng. Mặc định true. */
    activeOnly?: boolean
}

export function PackagingMaterialPicker({
    excludeIds = [],
    onPick,
    placeholder,
    disabled,
    excludeForPackaging = false,
    value,
    activeOnly = true,
}: Props) {
    const [search, setSearch] = useState('')
    const debouncedSearch = useDebounced(search, 300)

    const query = useInfinitePackagingMaterials({
        ...(activeOnly ? { is_active: true } : {}),
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
    })
    // Destructure ra primitive + hàm ổn định: truyền cả object query xuống
    // Combobox sẽ tạo callback mới mỗi render và làm observer ngắt/nối liên tục.
    const { hasNextPage, isFetchingNextPage, fetchNextPage, isLoading } = query

    const materials: PackagingMaterial[] = useMemo(
        () => query.data?.pages.flatMap((p) => p.items) ?? [],
        [query.data],
    )

    // Giá trị đã chọn sẵn có thể nằm ở trang chưa tải (VD mở lại dòng BOM cũ),
    // khi đó ô sẽ hiện placeholder thay vì tên vật tư. Chỉ gọi khi thật sự
    // thiếu, và chỉ khi picker có `value`.
    const foundSelected = materials.some((mt) => mt.id === value)
    const fallback = usePackagingMaterial(
        value,
        Boolean(value) && !foundSelected,
    )

    // Ghép bản ghi dự phòng vào đầu để trigger hiển thị đúng tên. Phải dùng
    // chung danh sách này cho cả options lẫn handleChange, nếu không chọn lại
    // chính bản ghi dự phòng sẽ im lặng không làm gì.
    //
    // Nhưng KHÔNG ghép khi đang tìm kiếm: nó không khớp từ khoá, hiện lên giữa
    // kết quả tìm sẽ trông như một kết quả hợp lệ. Lúc đó danh sách phải đúng
    // là những gì server trả về.
    const allMaterials = useMemo(
        () =>
            fallback.data && !foundSelected && !debouncedSearch
                ? [fallback.data, ...materials]
                : materials,
        [fallback.data, foundSelected, materials, debouncedSearch],
    )

    const excludeSet = useMemo(() => new Set(excludeIds), [excludeIds])
    const options = useMemo(
        () =>
            allMaterials
                .filter((mt) => !excludeSet.has(mt.id))
                .filter((mt) => !excludeForPackaging || !mt.is_for_packaging)
                .map((mt) => ({
                    value: mt.id,
                    // Nhãn vẫn là chuỗi phẳng: Combobox dùng nó cho phần hiển
                    // thị khi đã chọn. Phần dropdown mới dùng `render`.
                    label: `${mt.name} (${mt.sku})`,
                    render: () => (
                        <LotOptionRow
                            title={mt.name}
                            code={mt.sku}
                            chips={[mt.unit]}
                            qty={formatQty(mt.stock_available ?? 0)}
                        />
                    ),
                })),
        [allMaterials, excludeSet, excludeForPackaging],
    )

    // Ô tìm kiếm nằm trong popover bị Radix unmount khi đóng; reset state search
    // theo, nếu không mở lại thấy ô rỗng mà danh sách vẫn lọc theo từ khoá cũ.
    const handleOpenChange = (open: boolean) => {
        if (!open) setSearch('')
    }

    const handleChange = (id: string) => {
        const mat = allMaterials.find((mt) => mt.id === id)
        if (!mat) return
        onPick({
            id: mat.id,
            name: mat.name,
            sku: mat.sku,
            unit: mat.unit,
            sale_price: mat.sale_price ?? 0,
            is_for_packaging: mat.is_for_packaging,
            tracking_mode: mat.tracking_mode,
        })
    }

    return (
        <Combobox
            options={options}
            value={value ?? ''}
            onChange={handleChange}
            onSearchChange={setSearch}
            onOpenChange={handleOpenChange}
            shouldFilter={false}
            placeholder={placeholder ?? m.packaging_picker_placeholder()}
            searchPlaceholder={m.packaging_picker_search_placeholder()}
            emptyText={m.packaging_picker_empty()}
            loading={isLoading}
            disabled={disabled}
            className="w-full"
            onLoadMore={fetchNextPage}
            hasMore={hasNextPage}
            loadingMore={isFetchingNextPage}
        />
    )
}
